import React, { useState, useEffect } from 'react';
import {
  X,
  Database,
  CheckCircle2,
  AlertCircle,
  Copy,
  Check,
  RefreshCw,
  ExternalLink,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import {
  getStoredConfig,
  saveSupabaseCredentials,
  clearSupabaseCredentials,
  checkSupabaseConnection,
  SUPABASE_SQL_SCHEMA,
} from '../lib/supabase';
import { useApp } from '../context/AppContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const SupabaseConfigModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { refreshData, isSupabaseConnected } = useApp();
  const [supabaseUrl, setSupabaseUrl] = useState('');
  const [supabaseKey, setSupabaseKey] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'config' | 'sql' | 'guide'>('config');

  useEffect(() => {
    if (isOpen) {
      const config = getStoredConfig();
      setSupabaseUrl(config.url);
      setSupabaseKey(config.anonKey);
      setTestResult(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    saveSupabaseCredentials(supabaseUrl, supabaseKey);
    setTesting(true);
    setTestResult(null);
    const res = await checkSupabaseConnection();
    setTestResult(res);
    setTesting(false);
    if (res.success) {
      await refreshData();
    }
  };

  const handleClear = () => {
    clearSupabaseCredentials();
    setSupabaseUrl('');
    setSupabaseKey('');
    setTestResult({ success: false, message: 'ลบการตั้งค่าแล้ว ระบบจะทำงานในโหมดจำลองข้อมูลในเครื่อง' });
    refreshData();
  };

  const handleCopySql = () => {
    navigator.clipboard.writeText(SUPABASE_SQL_SCHEMA);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div id="supabase-modal-backdrop" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
      <div id="supabase-modal-container" className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-slate-900 text-white">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-emerald-500/20 text-emerald-400 rounded-lg">
              <Database className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">การเชื่อมต่อฐานข้อมูล Supabase</h3>
              <p className="text-xs text-slate-300">นราวดี อิลิแกนซ์ (Narawadee Elegance DB Integration)</p>
            </div>
          </div>
          <button
            id="close-supabase-modal-btn"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200 bg-slate-50 px-6 pt-2">
          <button
            id="tab-supabase-config"
            onClick={() => setActiveTab('config')}
            className={`pb-3 px-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'config'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <Zap className="w-4 h-4" />
            ตั้งค่า API Key
          </button>
          <button
            id="tab-supabase-sql"
            onClick={() => setActiveTab('sql')}
            className={`pb-3 px-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'sql'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <Database className="w-4 h-4" />
            SQL Schema Scripts
          </button>
          <button
            id="tab-supabase-guide"
            onClick={() => setActiveTab('guide')}
            className={`pb-3 px-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'guide'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
            คู่มือการเชื่อมต่อ
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === 'config' && (
            <form onSubmit={handleSave} className="space-y-4">
              <div className="p-3.5 bg-blue-50 border border-blue-100 rounded-xl text-blue-800 text-xs leading-relaxed flex items-start gap-2.5">
                <ShieldCheck className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">โหมดการทำงานแบบไฮบริด (Hybrid Storage)</p>
                  <p className="mt-0.5 text-blue-700">
                    หากยังไม่ได้กรอก Supabase ระบบจะบันทึกข้อมูลในเครื่อง (Local Storage) ให้ทดสอบใช้งานได้ครบทุกฟีเจอร์ เมื่อกรอก Supabase URL และ Anon Key ข้อมูลจะซิงค์กับคลาวด์อัตโนมัติ
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  SUPABASE PROJECT URL
                </label>
                <input
                  id="input-supabase-url"
                  type="text"
                  placeholder="https://xyzcompany.supabase.co"
                  value={supabaseUrl}
                  onChange={(e) => setSupabaseUrl(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  SUPABASE ANON KEY (Public Key)
                </label>
                <input
                  id="input-supabase-anon-key"
                  type="password"
                  placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                  value={supabaseKey}
                  onChange={(e) => setSupabaseKey(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono"
                />
              </div>

              {testResult && (
                <div
                  className={`p-3.5 rounded-xl text-xs flex items-start gap-2.5 ${
                    testResult.success
                      ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                      : 'bg-rose-50 border border-rose-200 text-rose-800'
                  }`}
                >
                  {testResult.success ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                  )}
                  <div>
                    <span className="font-semibold">{testResult.success ? 'สถานะ: เชื่อมต่อสำเร็จ' : 'สถานะ: ข้อผิดพลาด'}</span>
                    <p className="mt-0.5">{testResult.message}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  id="btn-clear-supabase"
                  onClick={handleClear}
                  className="px-3.5 py-2 text-xs font-medium text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                >
                  ล้างการตั้งค่า (ใช้โหมดจำลอง)
                </button>

                <div className="flex items-center gap-2">
                  <button
                    type="submit"
                    id="btn-save-supabase"
                    disabled={testing}
                    className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl shadow-sm transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {testing && <RefreshCw className="w-4 h-4 animate-spin" />}
                    {testing ? 'กำลังตรวจสอบ...' : 'บันทึกและเชื่อมต่อ Supabase'}
                  </button>
                </div>
              </div>
            </form>
          )}

          {activeTab === 'sql' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-slate-800">SQL สำหรับสร้างตารางบน Supabase</h4>
                  <p className="text-xs text-slate-500">
                    นำคำสั่งด้านล่างไปรันในเมนู <b>SQL Editor</b> บน Supabase Dashboard
                  </p>
                </div>
                <button
                  id="btn-copy-sql"
                  onClick={handleCopySql}
                  className="px-3.5 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium rounded-lg flex items-center gap-1.5 transition-colors shadow-sm"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'คัดลอกเรียบร้อยแล้ว!' : 'คัดลอก SQL'}
                </button>
              </div>

              <div className="relative rounded-xl overflow-hidden border border-slate-800 bg-slate-950 p-4">
                <pre className="text-xs font-mono text-emerald-400 overflow-x-auto max-h-72 leading-relaxed selection:bg-emerald-900">
                  {SUPABASE_SQL_SCHEMA}
                </pre>
              </div>

              <div className="p-3 bg-slate-100 rounded-xl text-slate-600 text-xs">
                💡 คำแนะนำ: ตารางที่ถูกสร้างได้แก่ <code>profiles</code>, <code>announcements</code>, <code>parcels</code>, <code>maintenance_tickets</code>, <code>bills</code>, <code>visitors</code>, <code>facility_bookings</code> พร้อมตั้งค่าสิทธิ์ Row Level Security (RLS)
              </div>
            </div>
          )}

          {activeTab === 'guide' && (
            <div className="space-y-3.5 text-xs text-slate-600">
              <h4 className="font-semibold text-slate-900 text-sm">ขั้นตอนการเชื่อมต่อกับ Supabase ฟรีใน 2 นาที:</h4>
              <ol className="list-decimal list-inside space-y-2 leading-relaxed">
                <li>
                  สมัครใช้งานหรือเข้าสู่ระบบที่{' '}
                  <a href="https://supabase.com" target="_blank" rel="noreferrer" className="text-blue-600 font-medium inline-flex items-center gap-0.5 hover:underline">
                    supabase.com <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>กด <b>New Project</b> แล้วตั้งชื่อโครงการ เช่น <span className="font-mono text-slate-800">Narawadee-Elegance</span></li>
                <li>ไปที่เมนู <b>SQL Editor</b> ทางซ้ายมือกด <b>New Query</b> แล้วนำสคริปต์จากแท็บ <b>SQL Schema Scripts</b> ไปวางแล้วกด <b>RUN</b></li>
                <li>ไปที่ <b>Project Settings → API</b> แล้วคัดลอก <b>Project URL</b> และ <b>anon / public key</b></li>
                <li>นำมากรอกในแท็บ <b>ตั้งค่า API Key</b> ด้านบนแล้วกด <b>บันทึกและเชื่อมต่อ</b> ข้อมูลทุกอย่างจะทำงานบนคลาวด์แบบเรียลไทม์!</li>
              </ol>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isSupabaseConnected ? 'bg-emerald-500' : 'bg-amber-400'}`} />
            <span>สถานะระบบปัจจุบัน: {isSupabaseConnected ? 'เชื่อมต่อ Supabase Cloud แล้ว' : 'ทำงานในโหมดจำลอง (Local State)'}</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium rounded-lg transition-colors"
          >
            ปิดหน้าต่าง
          </button>
        </div>
      </div>
    </div>
  );
};
